/* eslint-disable @typescript-eslint/no-explicit-any -- existing annotations; retyping would change compile behavior */
import { Download } from '@mui/icons-material';
import { Button } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HiCheck, HiChevronDown, HiClipboardList, HiX } from 'react-icons/hi';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';

import * as E from '../../../../../../../eligibility/Eligibility.styled';
import * as S from './AssessmentReport.styled';
import { useUser } from 'contexts/UserProvider';

import api from 'api';
import { Role } from 'data/constants/roles';
import useEnabledCountries from 'hooks/useEnabledCountries';
import { setAlert } from 'redux/alert';
import { RootState } from 'redux/store';
import { assessmentReportCountry } from 'utils/countries';
import { getCountryFlag } from 'utils/fetchassets';
import { addPageNumbersToPdf, blobToDataUrl } from 'utils/pdfHelpers';

interface Score {
    label: string;
    max_score: number;
    score: number;
    passed: boolean;
    explanation: Explanation;
}

interface PersonalInfoData {
    firstname: string;
    lastname: string;
    middlename: string;
    birth_date: Date | null;
}

const initialStatePI: PersonalInfoData = {
    firstname: '',
    lastname: '',
    middlename: '',
    birth_date: null,
};

interface UserData {
    firstname: string;
    lastname: string;
    middlename: string;
    file_number: number;
    role?: string;
}

const initialStateUser: UserData = {
    firstname: '',
    lastname: '',
    middlename: '',
    file_number: 0,
    role: '',
};

interface Explanation {
    text: string;
    table?: string[][];
}

export interface Assessment {
    region: string;
    country: string;
    program_name: string;
    program_type?: string;
    scores: Score[];
    summary: Summary;
    immigration_advice?: string;
}

interface Summary {
    score: number;
    max_score: number;
    required_score: number;
    passed: boolean;
}

export interface Program {
    country: string;
    eligible_programs: [
        {
            program_name: string;
            program_type: string;
            scores: [];
            region: string;
            country: string;
            summary: {
                score: number;
                max_score: number;
                required_score: number;
                passed: boolean;
            };
        },
    ];
}

export function getColor(score: number, maxScore: number, passed: boolean) {
    const _maxScore = Math.max(1, maxScore);
    if (passed === false) return '#dc2626';
    else if (maxScore === 0) return '#16a34a';
    else if (score / _maxScore >= 0.85) return '#16a34a';
    else if (score / _maxScore >= 0.7) return '#f59e0b';
    else return '#dc2626';
}

export function makeScore(score: number, maxScore: number, passed: boolean) {
    const _maxScore = Math.max(1, maxScore);
    if (passed === false) return 0;
    else if (maxScore === 0) return 96;
    else return Math.round((100 * score) / _maxScore);
}

const toPercent = (score: number, maxScore: number): number | null => {
    if (!maxScore) return null;
    return Math.round((100 * score) / maxScore);
};

interface AssessmentReportsProps {
    showDownloadButton?: boolean;
}

export default function AssessmentReports({ showDownloadButton = false }: AssessmentReportsProps) {
    const tableName = 'PersonalInfo';
    const userTableName = 'User';
    const params = useParams();
    const location = useLocation();
    const eligibilityData = location.state as Assessment[] | null;
    const includesVisaProcessPath = location.pathname.includes('/visa-process');
    const { id: clientFlowId, language_setting: language } = useSelector(
        (state: RootState) => state.user.entity
    );
    const paramsId = Number(params.id);
    const candidate_id = includesVisaProcessPath ? clientFlowId : paramsId;

    const [active, setActive] = useState<number>(0);
    const [selectedCountry, setSelectedCountry] = useState<number>(0);
    const [data, setData] = useState(initialStatePI);
    const [userInfo, setUserInfo] = useState(initialStateUser);
    const [results, setResults] = useState<Program[]>();
    const [assessment, setAssessment] = useState<Assessment | null>(null);
    const [collapsedCountries, setCollapsedCountries] = useState<Record<number, boolean>>({});
    const [isDownloading, setIsDownloading] = useState(false);
    const dispatch = useDispatch();
    const { codes, names: enabledCountryNames } = useEnabledCountries();
    const { user } = useUser();
    const { t } = useTranslation();

    useEffect(() => {
        setAssessment(null);
        if (candidate_id) {
            setData(initialStatePI);
            setUserInfo(initialStateUser);
            api.tableInput.fetchCandidateInputSales(setData, tableName, candidate_id);
            api.tableInput.fetchCandidateInputSales(setUserInfo, userTableName, candidate_id);
        } else {
            api.tableInput.fetchCandidateInput(setData, tableName);
        }
    }, [candidate_id]);

    useEffect(() => {
        fetchBestScoreAssessmentResults(language);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [candidate_id, enabledCountryNames, language]);

    const fetchBestScoreAssessmentResults = async (lang: string) => {
        setResults([]);
        try {
            if (candidate_id) {
                const res = await api.assessment.getAllQualifiedServices(candidate_id, lang);
                const allowed = new Set(enabledCountryNames);
                setResults(
                    (res.data ?? []).filter((item: { country?: string }) =>
                        allowed.has(item.country ?? '')
                    )
                );
            }
        } catch (e) {
            console.error(e);
        }
    };

    /* Sidebar data — either the eligibility data passed in location.state (a
     * flat list of programs) or the candidate's top-score results (grouped by
     * country). Normalize both into the same country-group shape. */
    const countryGroups = useMemo(() => {
        if (eligibilityData && eligibilityData.length) {
            return [
                {
                    country: eligibilityData[0]?.country ?? '',
                    programs: eligibilityData as unknown as Assessment[],
                },
            ];
        }
        if (results && results.length) {
            return results.map((r) => ({
                country: r.country,
                programs: (r.eligible_programs ?? []) as unknown as Assessment[],
            }));
        }
        return [];
    }, [eligibilityData, results]);

    const handleSelect = (idx: number, country: number) => {
        setSelectedCountry(country);
        setActive(idx);
        const programs = countryGroups[country]?.programs;
        if (programs && programs[idx]) {
            setAssessment(programs[idx]);
        }
    };

    const isActive = (idx: number, country: number) =>
        active === idx && country === selectedCountry;

    const toggleCountry = (idx: number) => {
        setCollapsedCountries((prev) => ({ ...prev, [idx]: !prev[idx] }));
    };

    const firstname = userInfo?.firstname || user?.firstname;
    const middlename = userInfo?.middlename || user?.middlename;
    const lastname = userInfo?.lastname || user?.lastname;
    const role = userInfo?.role || user?.role;
    const fileNumber = userInfo?.file_number || user?.file_number;

    const candidateName = [firstname, middlename, lastname].filter(Boolean).join(' ');

    const downloadAssessment = async () => {
        if (!candidate_id || isDownloading) {
            return;
        }

        try {
            const onScreenCountries = countryGroups.flatMap((group) => {
                const fromPrograms = (group.programs ?? [])
                    .map((program) => program.country)
                    .filter((name): name is string => Boolean(name));
                return fromPrograms.length > 0 ? fromPrograms : [group.country];
            });
            const country = assessmentReportCountry(codes, assessment?.country, onScreenCountries);
            if (!country) {
                dispatch(
                    setAlert({
                        message: t('assessmentReportDownloadSubtitle', {
                            lng: language,
                            defaultValue: 'Choose the destination country and report language.',
                        }),
                        type: 'info',
                    })
                );
                return;
            }

            setIsDownloading(true);
            const resp = await api.assessment.getAssessment(candidate_id, country, language);

            // responseType:'blob' means an error/text body also arrives as a Blob.
            // Verify the %PDF header before handing it to pdf-lib.
            const header = await resp.data.slice(0, 5).text();
            if (!header.startsWith('%PDF')) {
                const message = await resp.data.text();
                console.error('Assessment report was not a PDF:', message);
                return;
            }

            const numbered = await addPageNumbersToPdf(resp.data);
            const pdfBase64 = await blobToDataUrl(
                new Blob([numbered], { type: 'application/pdf' })
            );
            const link = document.createElement('a');
            link.href = pdfBase64;
            link.download = 'assessment_report.pdf';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error('Error downloading the report:', error);
        } finally {
            setIsDownloading(false);
        }
    };

    const birthDate = data?.birth_date
        ? new Date(data.birth_date).toLocaleString(language === 'EN' ? 'en-AU' : 'fr-FR', {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
          })
        : '';

    const programErrorMessage =
        role === Role.CANDIDATE
            ? t('assessmentCannotBePerformed', { lng: language })
            : t('noEligiblePrograms', { lng: language });

    return (
        <S.Page>
            <S.PageHeaderRow>
                <S.PageHeader>
                    <S.PageEyebrow>
                        {t('assessmentReport', {
                            lng: language,
                            defaultValue: 'Assessment Report',
                        })}
                    </S.PageEyebrow>
                    <S.PageTitle>
                        {candidateName ||
                            t('assessmentReport', {
                                lng: language,
                                defaultValue: 'Assessment Report',
                            })}
                    </S.PageTitle>
                    {fileNumber ? (
                        <S.PageSubtitle>
                            {t('fileNumber', { lng: language, defaultValue: 'File' })} #{fileNumber}
                        </S.PageSubtitle>
                    ) : null}
                </S.PageHeader>
                {showDownloadButton ? (
                    <Button
                        size="small"
                        variant="contained"
                        loading={isDownloading}
                        loadingPosition="start"
                        disabled={!candidate_id}
                        onClick={() => {
                            void downloadAssessment();
                        }}
                        startIcon={<Download fontSize="small" />}
                        aria-label={t('downloadReport', { lng: language })}
                        sx={{
                            fontSize: '0.75rem',
                            textTransform: 'none',
                            py: 0.75,
                            minWidth: 'auto',
                            justifyContent: 'center',
                            fontWeight: 600,
                            flexShrink: 0,
                            alignSelf: { xs: 'stretch', sm: 'center' },
                            width: { xs: '100%', sm: 'auto' },
                        }}
                    >
                        {t('downloadReport', { lng: language })}
                    </Button>
                ) : null}
            </S.PageHeaderRow>

            <S.Layout>
                <S.Sidebar>
                    {countryGroups.length === 0 ? (
                        <S.SidebarEmpty>{programErrorMessage}</S.SidebarEmpty>
                    ) : (
                        countryGroups.map((group, countryIdx) => {
                            const collapsed = Boolean(collapsedCountries[countryIdx]);
                            return (
                                <S.CountryGroup key={`${group.country}-${countryIdx}`}>
                                    <S.CountryHeader
                                        type="button"
                                        expanded={!collapsed}
                                        onClick={() => toggleCountry(countryIdx)}
                                    >
                                        {group.country ? (
                                            <img
                                                src={
                                                    window.location.protocol +
                                                    '//' +
                                                    window.location.host +
                                                    getCountryFlag(group.country)
                                                }
                                                alt={group.country}
                                            />
                                        ) : null}
                                        <S.CountryName>{group.country}</S.CountryName>
                                        <S.CountryChevron expanded={!collapsed}>
                                            <HiChevronDown />
                                        </S.CountryChevron>
                                    </S.CountryHeader>

                                    {!collapsed && (
                                        <S.ProgramList>
                                            {group.programs.map((program, i) => {
                                                const pct = makeScore(
                                                    program.summary.score,
                                                    program.summary.max_score,
                                                    program.summary.passed
                                                );
                                                const color = getColor(
                                                    program.summary.score,
                                                    program.summary.max_score,
                                                    program.summary.passed
                                                );
                                                return (
                                                    <S.ProgramItem
                                                        key={`${program.program_name}-${i}`}
                                                        type="button"
                                                        active={isActive(i, countryIdx)}
                                                        onClick={() => handleSelect(i, countryIdx)}
                                                    >
                                                        {program.program_type ? (
                                                            <S.ProgramType>
                                                                {program.program_type}
                                                            </S.ProgramType>
                                                        ) : null}
                                                        <S.ProgramItemBody>
                                                            <S.ProgramName>
                                                                {program.program_name}
                                                            </S.ProgramName>
                                                            <S.ProgramScorePill color={color}>
                                                                {pct}%
                                                            </S.ProgramScorePill>
                                                        </S.ProgramItemBody>
                                                    </S.ProgramItem>
                                                );
                                            })}
                                        </S.ProgramList>
                                    )}
                                </S.CountryGroup>
                            );
                        })
                    )}
                </S.Sidebar>

                <S.BodyCard>
                    {assessment ? (
                        <AssessmentBody
                            assessment={assessment}
                            candidateName={candidateName}
                            fileNumber={fileNumber}
                            birthDate={birthDate}
                        />
                    ) : (
                        <S.EmptyBody>
                            <S.EmptyBodyIcon>
                                <HiClipboardList />
                            </S.EmptyBodyIcon>
                            <S.EmptyBodyTitle>
                                {t('selectProgram', {
                                    lng: language,
                                    defaultValue: 'Select a program',
                                })}
                            </S.EmptyBodyTitle>
                            <S.EmptyBodyText>
                                {t('selectSidebarProgram', { lng: language })}
                            </S.EmptyBodyText>
                        </S.EmptyBody>
                    )}
                </S.BodyCard>
            </S.Layout>
        </S.Page>
    );
}

function AssessmentBody({
    assessment,
    candidateName,
    fileNumber,
    birthDate,
}: {
    assessment: Assessment;
    candidateName: string;
    fileNumber: number | string | undefined;
    birthDate: string;
}) {
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { t } = useTranslation();

    const passed = Boolean(assessment.summary?.passed);
    const summaryPct = toPercent(
        assessment.summary?.score ?? 0,
        assessment.summary?.max_score ?? 0
    );
    const heroColor = getColor(
        assessment.summary?.score ?? 0,
        assessment.summary?.max_score ?? 0,
        passed
    );

    const subtitle = [assessment.country, assessment.region].filter(Boolean).join(' · ');

    return (
        <>
            <S.BodyHeader>
                <S.BodyEyebrow>
                    {assessment.program_type ||
                        t('program', { lng: language, defaultValue: 'Program' })}
                </S.BodyEyebrow>
                <S.BodyTitle>{assessment.program_name}</S.BodyTitle>
                {subtitle ? <S.BodySubtitle>{subtitle}</S.BodySubtitle> : null}
            </S.BodyHeader>

            <S.MetaGrid>
                {fileNumber ? (
                    <S.MetaItem>
                        <S.MetaLabel>
                            {t('fileNumber', { lng: language, defaultValue: 'File Number' })}
                        </S.MetaLabel>
                        <S.MetaValue>{fileNumber}</S.MetaValue>
                    </S.MetaItem>
                ) : null}
                {candidateName ? (
                    <S.MetaItem>
                        <S.MetaLabel>
                            {t('candidateName', { lng: language, defaultValue: 'Candidate' })}
                        </S.MetaLabel>
                        <S.MetaValue>{candidateName}</S.MetaValue>
                    </S.MetaItem>
                ) : null}
                {birthDate ? (
                    <S.MetaItem>
                        <S.MetaLabel>
                            {t('dateOfBirth', { lng: language, defaultValue: 'Date of birth' })}
                        </S.MetaLabel>
                        <S.MetaValue>{birthDate}</S.MetaValue>
                    </S.MetaItem>
                ) : null}
            </S.MetaGrid>

            <E.ResultHero passed={passed}>
                <E.HeroBadge passed={passed}>
                    {passed ? (
                        <>
                            <HiCheck size={14} />
                            {t('likelyEligible', {
                                lng: language,
                                defaultValue: 'Likely Eligible',
                            })}
                        </>
                    ) : (
                        <>
                            <HiX size={14} />
                            {t('notEligible', {
                                lng: language,
                                defaultValue: 'Not Eligible',
                            })}
                        </>
                    )}
                </E.HeroBadge>
                {summaryPct !== null ? (
                    <E.HeroScore color={heroColor}>{summaryPct}%</E.HeroScore>
                ) : null}
                {assessment.summary?.max_score ? (
                    <E.HeroDetails>
                        {t('programScore', {
                            lng: language,
                            defaultValue: 'Program Score',
                        })}
                        : {assessment.summary.score}
                        {' / '}
                        {assessment.summary.max_score}
                        {assessment.summary.required_score ? (
                            <>
                                {' · '}
                                {t('required', {
                                    lng: language,
                                    defaultValue: 'Required',
                                })}
                                : {assessment.summary.required_score}
                            </>
                        ) : null}
                    </E.HeroDetails>
                ) : null}
            </E.ResultHero>

            {assessment.scores && assessment.scores.length > 0 ? (
                <E.CategoryList>
                    {assessment.scores.map((item, idx) => {
                        const statusLabel = item.passed
                            ? t('pass', { lng: language, defaultValue: 'Pass' })
                            : t('fail', { lng: language, defaultValue: 'Fail' });
                        return (
                            <E.CategoryItem key={`${item.label}-${idx}`}>
                                <E.CategoryHeader>
                                    <E.CategoryTitle>
                                        {idx + 1}. {item.label}
                                    </E.CategoryTitle>
                                    <E.CategoryBadge passed={item.passed}>
                                        {item.passed ? <HiCheck size={12} /> : <HiX size={12} />}
                                        {item.max_score ? (
                                            <>
                                                {item.score}/{item.max_score} · {statusLabel}
                                            </>
                                        ) : (
                                            statusLabel
                                        )}
                                    </E.CategoryBadge>
                                </E.CategoryHeader>
                                {item.explanation?.text ? (
                                    <E.CategoryExplanation>
                                        {item.explanation.text}
                                    </E.CategoryExplanation>
                                ) : null}
                            </E.CategoryItem>
                        );
                    })}
                </E.CategoryList>
            ) : (
                <E.EmptyScores>
                    {t('noCategoryScores', {
                        lng: language,
                        defaultValue: 'No category breakdown available for this program.',
                    })}
                </E.EmptyScores>
            )}

            {assessment.immigration_advice ? (
                <E.AdviceBlock>
                    <E.AdviceTitle>
                        {t('immigrationAdvice', {
                            lng: language,
                            defaultValue: 'Description',
                        })}
                    </E.AdviceTitle>
                    <E.AdviceBody>{assessment.immigration_advice}</E.AdviceBody>
                </E.AdviceBlock>
            ) : null}
        </>
    );
}
